// menubarCommands — the registry that lets ONE menubar own commands it does not
// implement.
//
// Why this exists: the app used to carry two overlapping menus. A floating
// "Menu ▾" at App level (reachable during a character load) and a menubar inside
// the blueprint editor panel (which unmounts whenever the editor does). The
// duplication was a symptom, not the disease — the real problem is that the
// menubar was OWNED by the editor, so it could not exist before the editor did.
//
// The fix inverts ownership. `AppMenuBar` mounts once, above everything, before
// any character request fires. Editor-scoped commands are *published* into this
// registry by whoever can perform them, and the menubar renders whatever is
// published. Nothing is registered → the item is still listed, but DISABLED with
// the reason inline (never hidden, never a popup, never a silent no-op).
//
// This is the same module-singleton + subscribe shape the rest of the app uses
// for cross-tree state (liveTuneStore, textureQuality, waypointPickerStore), and
// for the same reason: the consumer must not re-render the producer.
//
// Scope is per-window by construction — a window running its own app boot (a
// second tab) has its own module instance. A popped-out editor is NOT such a
// window: it is a portal of the main window's own instance (FloatingWindow
// popped mode), so this one registry keeps driving it. Every editor pop-out
// works that way now, including the material graph.
//
// No imports on purpose — this module is a leaf so `node --test` can drive it
// directly (see tests/unit/menubarCommands.test.mjs).

/** @typedef {{ commands?: Record<string, Function>, state?: Record<string, any> }} Publication */

/** sourceId → publication. Insertion-ordered, which is what resolves collisions. */
let _sources = new Map()
let _listeners = []

/** Cached merged view, invalidated on every mutation. */
let _snapshot = null

function merge() {
  const commands = {}
  const state = {}
  for (const [sourceId, pub] of _sources) {
    for (const [id, fn] of Object.entries(pub.commands ?? {})) {
      if (typeof fn !== 'function') {
        console.warn(`[menubarCommands] "${sourceId}" published a non-function command "${id}" — ignored`)
        continue
      }
      if (id in commands) {
        // Two sources claiming one command id is a bug, not a fallback: the
        // menubar would fire whichever registered last, so say so out loud.
        console.warn(`[menubarCommands] command "${id}" published by more than one source — "${sourceId}" wins`)
      }
      commands[id] = fn
    }
    Object.assign(state, pub.state ?? {})
  }
  return { commands, state }
}

function snapshot() {
  if (_snapshot == null) _snapshot = merge()
  return _snapshot
}

function notify() {
  _snapshot = null
  const s = snapshot()
  for (const fn of _listeners) fn(s)
}

export const menubarCommands = {
  /**
   * Publish (or re-publish) one source's commands + state. Idempotent by
   * `sourceId`: a re-publish REPLACES that source's previous entry rather than
   * merging into it, so a command the source stopped offering actually
   * disappears instead of lingering as a stale handler.
   *
   * @param {string} sourceId
   * @param {Publication} publication
   * @returns {() => void} unregister
   */
  register(sourceId, publication) {
    if (!sourceId) throw new Error('[menubarCommands] register needs a sourceId')
    _sources.set(sourceId, publication ?? {})
    notify()
    return () => menubarCommands.unregister(sourceId)
  },

  /** Drop a source. Unknown ids are a no-op (unmount ordering must not throw). */
  unregister(sourceId) {
    if (!_sources.delete(sourceId)) return
    notify()
  },

  /** Merged `{ commands, state }` across every source, in registration order. */
  get() {
    return snapshot()
  },

  /** True when `id` has a handler right now. */
  has(id) {
    return typeof snapshot().commands[id] === 'function'
  },

  /**
   * One published state value, or `fallback` when no source publishes it.
   * State is a read: an absent key means "nobody is claiming this", which the
   * menubar renders as a disabled/unchecked control.
   */
  stateOf(key, fallback = undefined) {
    const s = snapshot().state
    return key in s ? s[key] : fallback
  },

  /**
   * Invoke a command. Returns the handler's result, or `undefined` when nothing
   * is published — and warns, because a menu item that silently does nothing is
   * exactly the failure mode the disabled-with-reason rendering exists to
   * prevent. The menubar should never reach this path for an unpublished
   * command; if it does, that's the bug this warning names.
   */
  run(id, ...args) {
    const fn = snapshot().commands[id]
    if (typeof fn !== 'function') {
      console.warn(`[menubarCommands] no handler for "${id}" — nothing ran`)
      return undefined
    }
    return fn(...args)
  },

  /** Subscribe to the merged view. Fires immediately with the current snapshot. */
  subscribe(fn) {
    _listeners.push(fn)
    fn(snapshot())
    return () => { _listeners = _listeners.filter(l => l !== fn) }
  },

  /** Test seam — drop every source and listener. */
  _reset() {
    _sources = new Map()
    _listeners = []
    _snapshot = null
  },
}

// Debug/test global, following the window.__* convention (CLAUDE.md): e2e specs
// open editor surfaces via __MENUBAR__.run('editors.open', 'events') instead of
// synthesising menu clicks for a state change that isn't what they test.
if (typeof window !== 'undefined') {
  window.__MENUBAR__ = menubarCommands
}

export default menubarCommands
